/***
*
*   usePersistedTableState hook
*   Keep a Material React Table's view state in localStorage so it survives the
*   unmounts the table can't see: navigating away to a detail view and back, or
*   a refetch that blanks the data a render guard is keying on. MRT holds this
*   state internally, so the only way to keep it is to lift it out and hand it
*   back through the controlled `state` prop.
*
*   Restoring is the dangerous half. The stored blob is untrusted input -- an
*   older build wrote it, a column id has since been renamed, or someone edited
*   it in devtools -- and it is handed straight to MRT during render, where
*   there is no ErrorBoundary to catch a throw. So the read validates shapes,
*   drops ids the table no longer has, and repairs the cross-key invariants MRT
*   itself does not enforce, rather than trusting what it finds.
*
*   PROPS
*   storageKey: localStorage key this table's state lives under
*   defaults: state object to fall back to -- also the first-visit view, and the
*             declaration of which keys are persisted at all
*   version: bump whenever the persisted shape changes, to discard old blobs
*   columnIds: the table's current column ids. Restored sorting / filters /
*              grouping naming anything outside this list are dropped.
*
**********/

import { useState, useEffect, useMemo, useRef } from 'react';

// MRT state key -> the prop MRT calls when the user changes it. Every handler
// receives a TanStack Updater, which is either the next value or a
// (previous) => next function, so each one has to resolve that before merging.
// Exported so a test can pin the mapping: a typo here silently stops
// persisting one key, and nothing else would fail.
export const STATE_HANDLERS = {

  columnVisibility: 'onColumnVisibilityChange',
  sorting: 'onSortingChange',
  columnFilters: 'onColumnFiltersChange',
  globalFilter: 'onGlobalFilterChange',
  pagination: 'onPaginationChange',
  grouping: 'onGroupingChange',
  density: 'onDensityChange',
  showColumnFilters: 'onShowColumnFiltersChange',
  showGlobalFilter: 'onShowGlobalFilterChange',

}

// Restored keys whose entries name a column. A stale id here is not cosmetic:
// MRT's grouping alert banner does table.getColumn(id).columnDef.header with no
// guard, so one renamed column turns into a crash on every dashboard load.
const COLUMN_SCOPED_KEYS = ['sorting', 'columnFilters', 'grouping'];

function isPlainObject(value){

  return !!value && typeof value === 'object' && !Array.isArray(value)

}

/*
 * Does the stored value look like the default it is replacing? Guards the read
 * against a blob whose types have drifted -- a `sorting` that is a string
 * reaches TanStack's getSortedRowModel, which calls .filter() on it and throws
 * during render, blanking the page until site data is cleared.
 */
function sameShape(stored, fallback){

  if (Array.isArray(fallback)) return Array.isArray(stored);
  if (isPlainObject(fallback)) return isPlainObject(stored);
  if (fallback === null || fallback === undefined) return true;
  return typeof stored === typeof fallback;

}

export function readTableState(storageKey, version, defaults){

  try {

    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;

    const stored = JSON.parse(raw);
    if (!stored || stored.version !== version) return defaults;

    const state = stored.state;
    if (!isPlainObject(state)) return defaults;

    // Restore only keys the caller declared, so a stale or hand-edited blob
    // can't inject unrelated MRT state (isLoading: true would skeleton forever).
    const restored = { ...defaults };

    for (const key of Object.keys(defaults)){

      if (!(key in state)) continue;
      if (!sameShape(state[key], defaults[key])) continue;

      // Maps like columnVisibility merge per id rather than replacing wholesale,
      // so a column added later keeps its default until the user touches it.
      restored[key] = isPlainObject(defaults[key])
        ? { ...defaults[key], ...state[key] }
        : state[key];

    }

    return restored;

  }
  catch (err){

    // Corrupt value, or storage blocked entirely. Defaults keep the table up.
    return defaults;

  }
}

export function writeTableState(storageKey, version, state){

  try {

    localStorage.setItem(storageKey, JSON.stringify({ version, state }));
    return true;

  }
  catch (err){

    // Private mode or quota. The table still works, it just stops persisting.
    return false;

  }
}

/*
 * Repair the cross-key invariants MRT does not enforce itself. Both halves fix
 * states a user can genuinely reach and then not escape, which only became
 * permanent once this state started outliving the page:
 *
 *  - a filter whose column is gone (or was never there) -- see the crash above;
 *  - a filter applied while its input is hidden. MRT disables the search toggle
 *    while a term is set, but its 250ms debounce lands after the box is closed,
 *    so closing it mid-type strands a filter behind a disabled button. The
 *    column-filter toggle has no guard at all. Either way the table silently
 *    shows a subset of verifications with nothing on screen explaining why.
 */
export function reconcileTableState(state, columnIds){

  const known = Array.isArray(columnIds) && columnIds.length ? new Set(columnIds) : null;
  const next = { ...state };

  if (known){
    for (const key of COLUMN_SCOPED_KEYS){

      if (!Array.isArray(next[key])) continue;

      next[key] = next[key].filter(entry => known.has(
        typeof entry === 'string' ? entry : entry?.id
      ));

    }
  }

  if (next.columnFilters?.length) next.showColumnFilters = true;
  if (next.globalFilter) next.showGlobalFilter = true;

  return next;

}

/*
 * Resolve one TanStack Updater onto the state object. Returning `prev` itself
 * when nothing changed is load-bearing, not just an optimisation: MRT's search
 * field calls setGlobalFilter(undefined) from an effect whenever globalFilter
 * is already undefined, which without the identity return re-renders forever.
 */
export function applyStateUpdate(prev, key, updater){

  const next = typeof updater === 'function' ? updater(prev[key]) : updater;
  return next === prev[key] ? prev : { ...prev, [key]: next };

}

export function usePersistedTableState(storageKey, defaults, version = 1, columnIds){

  // Read once per storage identity, through both the validating read and the
  // invariant repair, so a restored blob can never be less safe than defaults.
  const load = useRef();
  load.current = () => reconcileTableState(
    readTableState(storageKey, version, defaults), columnIds
  );

  const [state, setState] = useState(() => load.current());

  // One handler per persisted key, each resolving the Updater it is handed.
  // Keys the caller did not declare are left to MRT's own internal state --
  // controlling a key we never persist would drop it on the next mount.
  const handlers = useMemo(() => {

    const bound = {};

    for (const [key, handlerName] of Object.entries(STATE_HANDLERS)){

      if (!(key in defaults)) continue;

      bound[handlerName] = updater =>
        setState(prev => applyStateUpdate(prev, key, updater));

    }

    return bound;

  }, [defaults]);

  // Never write straight after a load: `state` is either the blob just read
  // back or the defaults, which read the same as no key at all. And when the
  // identity changes (switching to another account's key), re-read for the new
  // key rather than writing the outgoing table's state over it.
  const identity = `${storageKey}|${version}`;
  const loadedFor = useRef(identity);
  const skipWrite = useRef(true);

  useEffect(() => {

    if (loadedFor.current !== identity){

      loadedFor.current = identity;
      skipWrite.current = true;
      setState(load.current());
      return;

    }

    if (skipWrite.current){

      skipWrite.current = false;
      return;

    }

    writeTableState(storageKey, version, state);

  }, [storageKey, version, identity, state]);

  return [state, handlers];

}
